import React from 'react';

import { type UUID, isDefined, isEmpty, truncate, valOrThrow } from '@nfctron/nexus-core';
import { type ExtendedComponent, UiButton, cn, useFormat } from '@nfctron/nexus-react';

import { Text } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';

import { CounterControl } from '@root/lib/components/CounterControl/CounterControl';
import { Gallery } from '@root/lib/components/Gallery/Gallery';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';

import { useCartStore, useCartStoreCtx, useCartStoreExtraProductsProvider } from '../../stores/cartStore';

import { ProductConfigItem } from './ProductConfigItem';

/**
 * ProductCardCarted component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const ProductCardCarted: React.FunctionComponent<ProductCardCartedProps> = ({ cartProductId, cartTicketId }) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, ProductCardCarted.displayName);
	const format = useFormat();
	const cartStoreCtx = useCartStoreCtx();
	const cartConfig = cartStoreCtx.useCartConfig();

	const { productGetters, groupGetters, sheetConfig, cartOps } = useCartStoreExtraProductsProvider();

	// Fetch initial product data
	const { ticket, extraProduct } = useCartStore((state) => {
		const res = state.getCartedTicket({ cartTicketId });
		return {
			ticket: res.ticket,
			extraProduct: valOrThrow(
				res.ticket.extraProducts.find((p) => p.cartProductId === cartProductId),
				'extra product not found with given cartTicketId',
			),
		};
	});

	const { extraProductConfig, productGroup, totalProductPrice, productIs, chipAmount } = React.useMemo(() => {
		const config = productGetters.getByIdWithLimit({
			ticketProductId: extraProduct.ticketProductId,
			ticketTypeId: ticket.ticketTypeId,
		});

		return {
			extraProductConfig: config,
			productGroup: isEmpty(config.productGroupId) ? undefined : groupGetters.getById({ productGroupId: config.productGroupId }),
			productIs: productGetters.getState({
				ticketProductId: extraProduct.ticketProductId,
				cartTicketId,
			}),
			chipAmount: cartOps.getChipAmount({
				cartProductId: extraProduct.cartProductId,
				cartTicketId,
			}),
			totalProductPrice: cartOps.getPrice({
				cartProductId: extraProduct.cartProductId,
				ticketProductId: extraProduct.ticketProductId,
				cartTicketId,
			}),
		};
	}, [productGetters, extraProduct.ticketProductId, extraProduct.cartProductId, ticket.ticketTypeId, groupGetters, cartTicketId, cartOps]);

	const willHaveConfiguration = React.useMemo(() => {
		const isConfigurationRequired = productGetters.isConfigRequired({
			ticketProductId: extraProduct.ticketProductId,
			ticketTypeId: ticket.ticketTypeId,
		});
		if (isConfigurationRequired) return true;
		if (Object.values(extraProductConfig.variants).length > 0) return true;
		return false;
	}, [extraProduct.ticketProductId, extraProductConfig.variants, productGetters, ticket.ticketTypeId]);

	const onSelectCallback = React.useCallback(() => {
		if (isEmpty(extraProductConfig.productGroupId)) {
			sheetConfig.setProductInput({
				cartTicketId,
				ticketProductId: extraProductConfig.ticketProductId,
				cartProductId: extraProduct.cartProductId,
			});
		} else {
			sheetConfig.setVariantsInput({
				cartTicketId,
				productGroupId: extraProductConfig.productGroupId,
				cartProductId: extraProduct.cartProductId,
			});
		}
	}, [cartTicketId, extraProduct.cartProductId, extraProductConfig, sheetConfig]);

	/** optionalText configurations */
	const optionalTextConfig = React.useMemo<{
		label: React.ReactNode;
		emptyText: React.ReactNode;
		value?: string;
	} | null>(() => {
		if (isEmpty(extraProductConfig.optionalTextRegex)) return null;

		/** specific cases (shipping-address for HradyCZ for example) */
		switch (extraProductConfig.optionalTextRegex) {
			case 'shipping-address': {
				return {
					label: t('optionalText.shippingAddress.label'),
					emptyText: t('optionalText.shippingAddressEmpty.text'),
					value: isDefined(extraProduct.optionalTextShippingAddress)
						? [
								extraProduct.optionalTextShippingAddress?.address,
								extraProduct.optionalTextShippingAddress?.city,
								extraProduct.optionalTextShippingAddress?.postalCode,
								extraProduct.optionalTextShippingAddress?.country,
							].join(', ')
						: undefined,
				};
			}
			/** default case */
			default: {
				return {
					label: t('optionalText.label'),
					emptyText: t('optionalTextEmpty.text'),
					value: extraProduct.optionalText,
				};
			}
		}
	}, [extraProduct.optionalText, extraProduct.optionalTextShippingAddress, extraProductConfig.optionalTextRegex, t]);

	return (
		<div className={cn(['flex max-w-full gap-3 rounded-md border border-gray-200 p-3'])}>
			{/* gallery */}
			{isDefined(extraProductConfig.gallery) ? (
				<Gallery gallery={extraProductConfig.gallery} className="hidden w-[80px] shrink-0 self-start sm:flex" height={80} controls={false} />
			) : (
				isDefined(productGroup) &&
				isDefined(productGroup.gallery) && (
					<Gallery gallery={productGroup.gallery} className="hidden w-[80px] shrink-0 self-start sm:flex" height={80} controls={false} />
				)
			)}

			{/* main content */}
			<div className="max-w-3/4">
				{isDefined(productGroup) && productGroup.variants.length > 0 ? (
					<React.Fragment>
						{/* product name */}
						<Text c="lucky-point" className="text-xl">
							{productGroup.name}
						</Text>
						{/* description */}
						{!isEmpty(productGroup.description) && (
							<Text size="xs" c="dimmed" className="mb-1 text-base leading-[18.6px]">
								{truncate(productGroup.description, 110)}
							</Text>
						)}
					</React.Fragment>
				) : (
					<React.Fragment>
						{/* product name */}
						<Text c="lucky-point" className="text-xl">
							{extraProductConfig.name}
						</Text>
						{/* description */}
						{!isEmpty(extraProductConfig.description) && (
							<Text size="xs" c="dimmed" className="mb-1 text-base leading-[18.6px]">
								{truncate(extraProductConfig.description, 110)}
							</Text>
						)}
					</React.Fragment>
				)}

				{/* configuration */}
				{willHaveConfiguration && (
					<div className="flex max-w-full flex-col gap-1.5">
						{/* any other configuration (combination of variant+value) */}
						{isDefined(productGroup) &&
							productGroup.variants.map((variant) => {
								const variantValue = variant.values.find((val) => val.valueId === extraProductConfig.variants[variant.variantId]);
								if (!isDefined(variantValue)) return null;
								return <ProductConfigItem key={variant.variantId} term={variant.name} value={variantValue.name} onSelect={onSelectCallback} />;
							})}
						{/* optional text */}
						{isDefined(optionalTextConfig) && (
							<ProductConfigItem
								className="max-w-[64px] truncate sm:max-w-[150px] lg:max-w-[200px]"
								term={optionalTextConfig.label}
								value={
									!isEmpty(optionalTextConfig.value) ? (
										<div className="truncate pt-0.5 leading-4">{optionalTextConfig.value}</div>
									) : (
										optionalTextConfig.emptyText
									)
								}
								onSelect={onSelectCallback}
							/>
						)}
						{/* chip credit top up amount */}
						{extraProduct.chips.length > 0 && (
							<ProductConfigItem term={t('chips.label')} value={format.price(chipAmount, cartConfig.currencyId)} onSelect={onSelectCallback} />
						)}
					</div>
				)}
			</div>

			{/* controls */}
			<div className="ml-auto flex flex-col items-end gap-2 pr-1">
				{/* product price */}
				<Text c="lucky-point" fz={20} fw={700}>
					{format.price(totalProductPrice, cartConfig.currencyId)}
				</Text>

				{/* counter control */}
				{extraProductConfig.quantityLimit > 1 ? (
					<CounterControl
						size="sm"
						className="mt-auto ml-auto"
						value={extraProduct.count}
						onDecrement={() => {
							if (extraProduct.count > 1) {
								cartOps.update({
									cartTicketId,
									cartProductId: extraProduct.cartProductId,
									update: { count: extraProduct.count - 1 },
								});
							} else {
								openConfirmModal({
									title: t('confirmRemoveProduct.title'),
									centered: true,
									children: (
										<Text size="md">
											{t('confirmRemoveProduct.text', {
												name: extraProductConfig.name,
											})}
										</Text>
									),
									labels: {
										confirm: t('confirmRemoveProduct.confirmButton.text'),
										cancel: t('confirmRemoveProduct.cancelButton.text'),
									},
									confirmProps: { color: 'pink' },
									cancelProps: { color: 'zinc', variant: 'light' },
									onConfirm: async () => {
										cartOps.remove({
											cartTicketId,
											cartProductId: extraProduct.cartProductId,
										});
									},
								});
							}
						}}
						incrementActionProps={{
							disabled: productIs.soldOut || productIs.quantityLimited,
						}}
						onIncrement={() =>
							cartOps.update({
								cartTicketId,
								cartProductId: extraProduct.cartProductId,
								update: { count: extraProduct.count + 1 },
							})
						}
					/>
				) : (
					<UiButton
						variant="unstyled"
						color="unstyled"
						size="xs"
						className="mt-auto bg-white text-pink-500"
						onClick={() => {
							openConfirmModal({
								title: t('confirmRemoveProduct.title'),
								centered: true,
								children: (
									<Text size="md">
										{t('confirmRemoveProduct.text', {
											name: extraProductConfig.name,
										})}
									</Text>
								),
								labels: {
									confirm: t('confirmRemoveProduct.confirmButton.text'),
									cancel: t('confirmRemoveProduct.cancelButton.text'),
								},
								confirmProps: { color: 'pink' },
								cancelProps: { color: 'zinc', variant: 'light' },
								onConfirm: async () => {
									cartOps.remove({
										cartTicketId,
										cartProductId: extraProduct.cartProductId,
									});
								},
							});
						}}
					>
						{t('removeProductButton.text')}
					</UiButton>
				)}
			</div>
		</div>
	);
};

/**
 * ProductCardCarted component display name
 * @type {string}
 */
ProductCardCarted.displayName = 'Event/Cart/ProductCardCarted';

/**
 * ProductCardCarted component props type
 * @export
 */
export type ProductCardCartedProps = ExtendedComponent<{
	cartProductId: UUID;
	cartTicketId: UUID;
}>;
